import * as React from 'react'
import type { GhlConnectionDto, GhlFunnelMap, GhlFunnelSource } from '@lib/dto/gohighlevel.dto'
import type { LeadStatsChoicesDto } from '@lib/dto/lead-stats.dto'
import { FUNNEL_TARGET_LABELS, GHL_FUNNEL_TARGETS, type GhlFunnelTarget } from '@lib/enums/funnel-stage.enum'
import { GHL_APPOINTMENT_STATUS_LABEL } from '@lib/enums/ghl-appointment-status.enum'
import { GHL_OPPORTUNITY_STATUS_LABEL } from '@lib/enums/ghl-opportunity-status.enum'
import { funnelMapIncomplete } from '@lib/helpers/ghl-funnel-map'
import { normalizeGhlName } from '@lib/helpers/lead-stats-names'
import type { UpdateGhlConnectionInput } from '@lib/input/gohighlevel.input'
import { AppointmentsDefinitionFields, LeadsDefinitionFields, NameField } from '@/components/lead-stats/DefinitionFields'
import { Button } from '@/components/ui/button'
import { ChoiceCard, ChoiceGroup } from '@/components/ui/choice-card'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useGhlFunnelChoices } from '@/hooks/useGoHighLevel'

/**
 * A mapping as text in a fixed order, so two equal mappings read alike: Postgres
 * hands a saved one back with its object keys in an order of its own.
 */
export function canonicalFunnelMap(map: GhlFunnelMap): string {
  const stages = GHL_FUNNEL_TARGETS.flatMap((target) => {
    const source = map.stages[target]
    if (source === undefined) return []
    if (source.kind === 'appointments') {
      const d = source.definition
      return [[target, 'appointments', d.calendars, d.statuses, d.countBy]]
    }
    const d = source.definition
    return [[target, 'leads', d.stages, d.results, d.tagsAny, d.tagsNone, d.sources]]
  })
  return JSON.stringify([map.pipelines, stages])
}

/** "a", "a or b", "a, b or c". */
function orList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1] ?? ''}`
}

/** A mapped stage in a line: "Stage is Tour Completed", "Wedding Tour Calendar, by day booked". */
export function funnelSourceSummary(source: GhlFunnelSource): string {
  if (source.kind === 'appointments') {
    const d = source.definition
    const statuses = d.statuses.length > 0 ? ` (${orList(d.statuses.map((s) => GHL_APPOINTMENT_STATUS_LABEL[s]))})` : ''
    return `${orList(d.calendars)}${statuses}, ${d.countBy === 'booked' ? 'by day booked' : 'by the day it is for'}`
  }
  const d = source.definition
  const parts = [
    ...(d.stages.length > 0 ? [`stage is ${orList(d.stages)}`] : []),
    ...(d.results.length > 0 ? [`result is ${orList(d.results.map((r) => GHL_OPPORTUNITY_STATUS_LABEL[r]))}`] : []),
    ...(d.tagsAny.length > 0 ? [`tagged ${orList(d.tagsAny)}`] : []),
    ...(d.tagsNone.length > 0 ? [`not tagged ${orList(d.tagsNone)}`] : []),
    ...(d.sources.length > 0 ? [`from ${orList(d.sources)}`] : []),
  ]
  const text = parts.join(', ')
  return text === '' ? 'Nothing chosen yet' : text.charAt(0).toUpperCase() + text.slice(1)
}

const emptyLeads = (): GhlFunnelSource => ({
  kind: 'leads',
  definition: { stages: [], results: [], tagsAny: [], tagsNone: [], sources: [] },
})
const emptyAppointments = (): GhlFunnelSource => ({
  kind: 'appointments',
  definition: { calendars: [], statuses: [], countBy: 'booked' },
})

/**
 * What a stage starts as when it is first set up. Lost starts from GoHighLevel's
 * own "lost" result; nothing else is guessed, and long-term nurture is never Lost
 * unless someone says so (docs/funnel-stages.spec.md).
 */
export function newFunnelSource(target: GhlFunnelTarget): GhlFunnelSource {
  if (target !== 'lost') return emptyLeads()
  return { kind: 'leads', definition: { stages: [], results: ['lost'], tagsAny: [], tagsNone: [], sources: [] } }
}

/** The stages with one target's source replaced, or taken out when `source` is undefined. */
function withSource(
  stages: GhlFunnelMap['stages'],
  target: GhlFunnelTarget,
  source: GhlFunnelSource | undefined,
): GhlFunnelMap['stages'] {
  const next: GhlFunnelMap['stages'] = {}
  for (const t of GHL_FUNNEL_TARGETS) {
    const kept = t === target ? source : stages[t]
    if (kept !== undefined) next[t] = kept
  }
  return next
}

/** The choices narrowed to the pipelines whose leads are couples, so stages come only from those. */
function inPipelines(choices: LeadStatsChoicesDto, pipelines: readonly string[]): LeadStatsChoicesDto {
  if (pipelines.length === 0) return choices
  const wanted = new Set(pipelines.map(normalizeGhlName))
  return { ...choices, pipelines: choices.pipelines.filter((p) => wanted.has(normalizeGhlName(p.name))) }
}

interface GhlFunnelMapEditorProps {
  venueId: string
  connection: GhlConnectionDto
  busy: boolean
  onSave: (input: UpdateGhlConnectionInput, onSaved?: () => void) => void
}

/**
 * Which of this sub-account's stages, results, tags and calendars mean each step
 * a couple takes (docs/funnel-stages.spec.md), for the pipeline report. A step
 * left alone is "not tracked" from here, never zero. Lead stats aren't affected.
 * The whole mapping is saved at once.
 */
export function GhlFunnelMapEditor({ venueId, connection, busy, onSave }: GhlFunnelMapEditorProps) {
  const choices = useGhlFunnelChoices(venueId, connection.id)
  const [draft, setDraft] = React.useState<GhlFunnelMap>(connection.funnelMap)
  const [editing, setEditing] = React.useState<GhlFunnelTarget | null>(null)
  const [problem, setProblem] = React.useState<string | null>(null)
  const dirty = canonicalFunnelMap(draft) !== canonicalFunnelMap(connection.funnelMap)

  if (choices.isPending) return <p className="m-0 text-sm text-ink-muted">Reading this sub-account’s stages…</p>
  if (choices.isError) return <FormError>{requestErrorMessage(choices.error)}</FormError>

  const data = choices.data
  const scoped = inPipelines(data, draft.pipelines)

  function setSource(target: GhlFunnelTarget, source: GhlFunnelSource | undefined): void {
    setProblem(null)
    setDraft((d) => ({ ...d, stages: withSource(d.stages, target, source) }))
  }

  function save(): void {
    const incomplete = funnelMapIncomplete(draft)
    if (incomplete !== null) {
      setProblem(incomplete)
      return
    }
    setProblem(null)
    onSave({ funnelMap: draft }, () => setEditing(null))
  }

  return (
    <section className="space-y-3 border-t border-line pt-4" aria-labelledby={`funnel-${connection.id}`}>
      <div className="space-y-1">
        <p id={`funnel-${connection.id}`} className="m-0 text-sm font-medium text-ink">
          Funnel stages
        </p>
        <p className="m-0 text-xs text-ink-muted">
          Which of this sub-account’s stages, tags and calendars mean each step a couple takes, for the pipeline report.
          A step left alone shows as not tracked. Lead stats aren’t affected.
        </p>
      </div>

      {!data.read ? (
        <p className="m-0 text-sm text-ink-muted">Funnel stages can be set up after this connection’s first read.</p>
      ) : (
        <>
          <NameField
            id={`funnel-${connection.id}-pipelines`}
            label="Pipelines with couples"
            hint="Leave empty to count every pipeline."
            available={data.pipelines.map((p) => p.name)}
            chosen={draft.pipelines}
            onChange={(pipelines) => {
              setProblem(null)
              setDraft((d) => ({ ...d, pipelines }))
            }}
            placeholder="Every pipeline"
          />

          <ul className="m-0 list-none divide-y divide-line p-0">
            {GHL_FUNNEL_TARGETS.map((target) => (
              <FunnelStageRow
                key={target}
                idPrefix={`funnel-${connection.id}-${target}`}
                target={target}
                source={draft.stages[target]}
                choices={scoped}
                editing={editing === target}
                onEdit={() => setEditing(target)}
                onDone={() => setEditing(null)}
                onSetUp={() => {
                  setSource(target, newFunnelSource(target))
                  setEditing(target)
                }}
                onChange={(source) => setSource(target, source)}
                onRemove={() => {
                  setSource(target, undefined)
                  if (editing === target) setEditing(null)
                }}
              />
            ))}
          </ul>

          {problem !== null ? <FormError>{problem}</FormError> : null}
          <div className="flex gap-2">
            <Button type="button" size="sm" disabled={!dirty || busy} onClick={save}>
              Save funnel stages
            </Button>
            {dirty ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  setDraft(connection.funnelMap)
                  setEditing(null)
                  setProblem(null)
                }}
              >
                Discard changes
              </Button>
            ) : null}
          </div>
        </>
      )}
    </section>
  )
}

interface FunnelStageRowProps {
  idPrefix: string
  target: GhlFunnelTarget
  source: GhlFunnelSource | undefined
  choices: LeadStatsChoicesDto
  editing: boolean
  onEdit: () => void
  onDone: () => void
  onSetUp: () => void
  onChange: (source: GhlFunnelSource) => void
  onRemove: () => void
}

/** One funnel stage: not tracked, a line saying what it is, or its fields while being edited. */
function FunnelStageRow({ idPrefix, target, source, choices, editing, onEdit, onDone, onSetUp, onChange, onRemove }: FunnelStageRowProps) {
  const label = FUNNEL_TARGET_LABELS[target]

  function renderStatus(): React.ReactNode {
    if (source === undefined) {
      return (
        <span className="flex items-center gap-2 text-sm text-ink-muted">
          Not tracked
          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={onSetUp}>
            Set up
          </Button>
        </span>
      )
    }
    return (
      <span className="flex min-w-0 items-center gap-3 text-sm">
        <span className="min-w-0 truncate text-ink-muted">{funnelSourceSummary(source)}</span>
        {editing ? null : (
          <Button type="button" variant="link" size="sm" className="h-auto shrink-0 p-0" onClick={onEdit}>
            Edit
          </Button>
        )}
        <Button type="button" variant="link" size="sm" className="h-auto shrink-0 p-0" onClick={onRemove}>
          Remove
        </Button>
      </span>
    )
  }

  return (
    <li className="space-y-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="shrink-0 text-sm font-medium text-ink">{label}</span>
        {renderStatus()}
      </div>
      {editing && source !== undefined ? (
        <div className="space-y-3 rounded-[var(--radius-control)] border border-line p-3">
          {/* Lost comes only from leads: a couple is lost while a lead matches, and reopened when it stops. */}
          {target === 'lost' ? null : (
            <ChoiceGroup legend={`What ${label} is`} hideLegend columns={2}>
              <ChoiceCard
                type="radio"
                size="compact"
                name={`${idPrefix}-kind`}
                checked={source.kind === 'leads'}
                onSelect={() => onChange(emptyLeads())}
                title="Lead stages, results and tags"
              />
              <ChoiceCard
                type="radio"
                size="compact"
                name={`${idPrefix}-kind`}
                checked={source.kind === 'appointments'}
                onSelect={() => onChange(emptyAppointments())}
                title="Calendar appointments"
              />
            </ChoiceGroup>
          )}
          {source.kind === 'leads' ? (
            <LeadsDefinitionFields
              idPrefix={idPrefix}
              definition={source.definition}
              choices={choices}
              onChange={(definition) => onChange({ kind: 'leads', definition })}
            />
          ) : (
            <AppointmentsDefinitionFields
              idPrefix={idPrefix}
              definition={source.definition}
              choices={choices}
              onChange={(definition) => onChange({ kind: 'appointments', definition })}
            />
          )}
          <Button type="button" size="sm" variant="outline" onClick={onDone}>
            Done
          </Button>
        </div>
      ) : null}
    </li>
  )
}
